import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { useUnsafeCallback, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Pencil } from '@factorialco/f0-react/icons/app'
import {
  CostCentersListDocument,
  CostCentersListQuery,
  CostCentersValueObjectsSourceTypeEnum,
  CostCentersValueObjectsCostCenterStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
// TODO: remove - imported to trigger codegen inclusion for mobile compatibility
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { useGraphqlChildrenDataCollection } from 'modules/core/hooks/useGraphqlChildrenDataCollection'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { CostCenterEditModal } from 'modules/cost_centers/components/CostCenterNestedList/Edit'
import { COST_CENTER_STATUS } from 'modules/cost_centers/lib/consts'

import { CostCenterConfigurations, CostCenterDetails } from 'modules/cost_centers/lib/types'
import { useWorkplaceFilter } from 'modules/employees/lib/filters/workplace'

const getCostCenterStatus = (status: string) => {
  if (status === COST_CENTER_STATUS.active) return 'positive'
  return 'warning'
}

const getCostCenterStatusLabel = (status: CostCentersValueObjectsCostCenterStatusEnum) => {
  if (status === COST_CENTER_STATUS.active)
    return i18n.t('finance.cost_centers_list.filters.status.active')
  if (status === COST_CENTER_STATUS.inactive)
    return i18n.t('finance.cost_centers_list.filters.status.inactive')
  return '-'
}

const getCostCenterDimensionName = (item: CostCenterDetails): string => {
  if (!item.dimensions || item.dimensions.length === 0) {
    return item.name
  }

  const parts: string[] = []

  for (const dimension of item.dimensions) {
    if (dimension.jobCatalogFamilyName) parts.push(dimension.jobCatalogFamilyName)
    if (dimension.jobCatalogFunctionName) parts.push(dimension.jobCatalogFunctionName)
    if (dimension.jobCatalogRoleName) parts.push(dimension.jobCatalogRoleName)
    if (dimension.workplaceName) parts.push(dimension.workplaceName)
  }

  return parts.length > 0 ? parts.join(' - ') : item.name
}

const CostCentersNestedList = ({ canManageCostCenters }: { canManageCostCenters: boolean }) => {
  const { company } = useCurrentSession()
  const { configurations } = useOutletContext<{
    configurations: CostCenterConfigurations
  }>()
  const [costCenterToEdit, setCostCenterToEdit] = useState<CostCenterDetails | null>(null)
  const workplaceFilter = useWorkplaceFilter()

  const handleEditAsset = useUnsafeCallback((item: CostCenterDetails) => {
    setCostCenterToEdit(item)
  }, [])

  const hasWorkplaceConfig = useUnsafeMemo(() => {
    return configurations.some((config) => config.structure === 'workplace')
  }, [configurations])

  const jobCatalogStructure = useUnsafeMemo(() => {
    const jcConfig = configurations.find((config) => config.structure.startsWith('job_catalog_'))
    return jcConfig?.structure
  }, [configurations])

  const shouldShowJobCatalogColumns = useUnsafeMemo(() => {
    return {
      family:
        jobCatalogStructure === 'job_catalog_family' ||
        jobCatalogStructure === 'job_catalog_function' ||
        jobCatalogStructure === 'job_catalog_role',
      function:
        jobCatalogStructure === 'job_catalog_function' ||
        jobCatalogStructure === 'job_catalog_role',
      role: jobCatalogStructure === 'job_catalog_role',
    }
  }, [jobCatalogStructure])

  const hideFilters = useUnsafeMemo(() => !configurations?.length, [configurations])

  const mapData = useUnsafeCallback((data: CostCentersListQuery) => {
    return data.costCenters.costCentersConnection.edges.map((edge) => edge.node)
  }, [])

  const sourceType: CostCentersValueObjectsSourceTypeEnum = 'structure_based'

  const dataSourceOptions = useUnsafeMemo(
    () => ({
      document: CostCentersListDocument,
      mapData: (tableData: CostCentersListQuery) => mapData(tableData),
      paginationType: 'infinite-scroll' as const,
      perPage: 20,
      sortings: {
        name: {
          label: i18n.t('finance.account_list.headers.name'),
          sortBy: 'name',
        },
      },
      defaultSortings: {
        field: 'name' as const,
        order: 'asc' as const,
      },
      search: {
        enabled: !hideFilters,
      },
      ...(hideFilters
        ? {}
        : {
            filters: {
              legalEntityIds: {
                type: 'in' as const,
                label: i18n.t('finance.cost_centers_list.filters.legal_entity'),
                options: {
                  options: company.legalEntities.map(({ id, legalName }) => ({
                    value: id,
                    label: legalName || '',
                  })),
                },
              },
              workplaceIds: {
                ...workplaceFilter,
                label: i18n.t('finance.cost_centers_list.filters.workplace'),
              },
              statuses: {
                type: 'in' as const,
                label: i18n.t('finance.cost_centers_list.filters.status.label'),
                options: {
                  options: [
                    {
                      value: 'active' as const,
                      label: i18n.t('finance.cost_centers_list.filters.status.active'),
                    },
                    {
                      value: 'inactive' as const,
                      label: i18n.t('finance.cost_centers_list.filters.status.inactive'),
                    },
                  ],
                },
              },
            },
          }),
      ...(hideFilters
        ? {}
        : {
            presets: [
              {
                label: i18n.t('finance.cost_centers_list.filters.status.active'),
                filter: { statuses: ['active' as const] },
              },
              {
                label: i18n.t('finance.cost_centers_list.filters.status.inactive'),
                filter: { statuses: ['inactive' as const] },
              },
              {
                label: i18n.t('finance.cost_centers_list.filters.all_legal_entities'),
                filter: { withoutLegalEntities: true },
              },
            ],
          }),
      mapVariables: ({
        search,
        filters,
        sortings,
      }: {
        search?: string
        sortings?: Array<{ field: string; order: string }>
        filters?: {
          legalEntityIds?: number[]
          workplaceIds?: number[]
          statuses?: CostCentersValueObjectsCostCenterStatusEnum[]
          sourceType?: CostCentersValueObjectsSourceTypeEnum
          withoutLegalEntities?: boolean
        }
      }) => {
        const workplaceIds = Array.isArray(filters?.workplaceIds)
          ? filters.workplaceIds.map(Number).filter(Boolean)
          : undefined

        return {
          sourceType,
          search: search,
          sortOrder: sortings?.[0],
          statuses: filters?.statuses,
          legalEntityIds: filters?.legalEntityIds,
          withoutLegalEntities: filters?.withoutLegalEntities,
          ...(workplaceIds?.length && { workplaceIds }),
        }
      },
      mapPagination: (tableData: CostCentersListQuery) => ({
        total: tableData.costCenters.costCentersConnection.totalCount ?? 0,
        cursor: tableData.costCenters.costCentersConnection.pageInfo.endCursor ?? '0',
        hasNextPage: tableData.costCenters.costCentersConnection.pageInfo.hasNextPage ?? false,
      }),
      totalItemSummary: (totalCount: number) =>
        i18n.tp('finance.cost_centers_list.count_cost_centers', { count: totalCount }),
      itemOnClick: (item: CostCenterDetails) => () => {
        if (canManageCostCenters) {
          handleEditAsset(item)
        }
      },
      itemActions: canManageCostCenters
        ? (item: CostCenterDetails) => {
            const actions = [
              {
                label: i18n.t('finance.cost_centers_list.popover.edit'),
                icon: Pencil,
                onClick: () => {
                  handleEditAsset(item)
                },
                critical: false,
              },
            ]
            return actions
          }
        : undefined,
    }),
    [company, hideFilters, canManageCostCenters, workplaceFilter]
  )

  const source = useGraphqlDataCollectionSource(dataSourceOptions)

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  sorting: 'name',
                  width: 400,
                  label: i18n.t('finance.cost_centers_list.headers.name'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'text',
                      value: getCostCenterDimensionName(item),
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.code'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'text',
                      value: item.code || '-',
                    }
                  },
                },
                ...(shouldShowJobCatalogColumns.family
                  ? [
                      {
                        label: i18n.t('finance.cost_centers_list.headers.job_catalog.family'),
                        render: (item: CostCenterDetails) => {
                          const familyName = item.dimensions
                            ?.map((d) => d.jobCatalogFamilyName)
                            .filter(Boolean)
                            .join(', ')
                          return {
                            type: 'text' as const,
                            value: familyName || '-',
                          }
                        },
                      },
                    ]
                  : []),
                ...(shouldShowJobCatalogColumns.function
                  ? [
                      {
                        label: i18n.t('finance.cost_centers_list.headers.job_catalog.function'),
                        render: (item: CostCenterDetails) => {
                          const functionName = item.dimensions
                            ?.map((d) => d.jobCatalogFunctionName)
                            .filter(Boolean)
                            .join(', ')
                          return {
                            type: 'text' as const,
                            value: functionName || '-',
                          }
                        },
                      },
                    ]
                  : []),
                ...(shouldShowJobCatalogColumns.role
                  ? [
                      {
                        label: i18n.t('finance.cost_centers_list.headers.job_catalog.role'),
                        render: (item: CostCenterDetails) => {
                          const roleName = item.dimensions
                            ?.map((d) => d.jobCatalogRoleName)
                            .filter(Boolean)
                            .join(', ')
                          return {
                            type: 'text' as const,
                            value: roleName || '-',
                          }
                        },
                      },
                    ]
                  : []),
                ...(hasWorkplaceConfig
                  ? [
                      {
                        label: i18n.t('finance.cost_centers.assignments.structured.workplace'),
                        render: (item: CostCenterDetails) => {
                          const workplaceName = item.dimensions
                            ?.map((d) => d.workplaceName)
                            .filter(Boolean)
                            .join(', ')
                          return {
                            type: 'text' as const,
                            value: workplaceName || '-',
                          }
                        },
                      },
                    ]
                  : []),
                {
                  label: i18n.t('finance.cost_centers_list.headers.legal_entity'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'text',
                      value:
                        item.legalEntity?.legalName ||
                        i18n.t('finance.cost_centers_list.legal_entity_unassigned'),
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.status'),
                  width: 200,
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'status',
                      value: {
                        status: getCostCenterStatus(item.status),
                        label: getCostCenterStatusLabel(item.status),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      {costCenterToEdit && (
        <CostCenterEditModal
          onClose={() => setCostCenterToEdit(null)}
          onSuccess={() => {
            setCostCenterToEdit(null)
          }}
          costCenter={costCenterToEdit}
          configurations={configurations}
          hasNewStructureEnabled
        />
      )}
    </>
  )
}

export { CostCentersNestedList }
